import {Component, EventEmitter, Input, Output, SimpleChanges} from '@angular/core';
import {FormBuilder, FormGroup, Validators} from '@angular/forms';
import {GuestFormType} from '@employee-pages/badge-request-page/employee-member-firm-visitor/add-guest/guest-management';
import {AutomaticCroppingService} from '@ui-app/services/automatic-cropping.service';
import {EmployeeBusinessService} from '../../../../core/services/business.service';
import {ImageService} from '@ui-app/services/image.service';
import {INonDeloitteGuestRequestWithPhoto} from '@employee-pages/badge-request-page/non-deloitte-visitors/non-deloitte.const';
import {REGEX_EMAIL_FORMAT, REGEX_PHONE_10DIGIT} from '@employee-models/common.enums';
import {IContractorRequest} from '@employee-pages/badge-request-page/usi-employee-badge-forms';
import {
    AddNoneDeloitteComponent
} from '@employee-pages/badge-request-page/non-deloitte-visitors/add-none-deloitte/add-none-deloitte.component';
import { ISearchEmployeeApprovers } from '@employee-models/common.models';
import { PhotoData } from '@shared-models/common.models';
import * as EmployeeModels from '@employee-models/index';
import { IBadgeRequestPhotoInfo } from '@employee-models/data.models';
import { NonDeloittePurposeOfVisitEnum, NonDeloitteTypeOfVisitEnum, UserRoleEnum } from '@employee-models/data.enums';
import { ToastrService } from 'ngx-toastr';
import { Router } from '@angular/router';

@Component({
    selector: 'employee-edit-none-deloitte',
    templateUrl: './add-none-deloitte.component.html',
    styleUrl: './add-none-deloitte.component.less'
})
export class EditNoneDeloitteComponent extends AddNoneDeloitteComponent {
    @Output() guestEdited = new EventEmitter<any>();
    @Input() guestData: INonDeloitteGuestRequestWithPhoto;
    @Output() formDirty = new EventEmitter<boolean>();
    @Input() isBadgeExtension: boolean = false;
    user: any;
    selectedApprovers = [];
    selectedIRCApprovers = [];
    title = 'Edit contractor';
    buttonSubmitTitle = 'Save';
    guestFormType = GuestFormType.EDIT;

    // @ts-ignore
    addGuestForm: FormGroup<any>;

    constructor(protected fb: FormBuilder,
                automaticCroppingService: AutomaticCroppingService,
                employeeBusinessService: EmployeeBusinessService,
                imageService: ImageService,
                private toastr: ToastrService,
                private router: Router) {
    super(fb, automaticCroppingService, employeeBusinessService, imageService, toastr, router);
        this.employeeBusinessService.userProfileService.userData$.subscribe(data => this.user = data);
    }

    ngOnInit(): void {
        this.createForm();
        if (this.guestData?.photoInfo) {
            this.userPhotoUrl = this.guestData?.photoInfo['file'].url;
            this.photoData = this.guestData.photoInfo['file'];
        }
        this.attachmentData = this.guestData.attachmentGuid;

        this.setupCropImage();
        this.setupImageResize();
    }

    createForm(): void {
        const normalizedLocalCellNumber = (this.guestData.localCellNumber || '')
            .replace(/^\+91-?/, '')
            .trim();
        // @ts-ignore
        this.addGuestForm = this.fb.group({
            firstName: [this.guestData.firstName, [Validators.required]],
            lastName: [this.guestData.lastName, [Validators.required]],
            companyName: [this.guestData.companyName, [Validators.required]],
            email: [this.guestData.email, [Validators.required, Validators.pattern(REGEX_EMAIL_FORMAT)]],
            localCellNumber: [normalizedLocalCellNumber, [Validators.required, Validators.pattern(REGEX_PHONE_10DIGIT)]],
            cardNumber: [this.guestData.cardNumber || '', this.isBadgeExtension ? [Validators.required, Validators.pattern(/^\d+$/)] : []],
            accessRequiredIds: [this.guestData.accessRequiredIds, [Validators.required]],
            hasLapTop: [this.guestData.hasLapTop],
            laptopMake: [this.guestData.laptopMake],
            laptopSerialNumber: [this.guestData.laptopSerialNumber],
            visitStartDate: [this.guestData.visitStartDate, [Validators.required, this.validateNotBeforeToday]],
            visitEndDate: [this.guestData.visitEndDate, [Validators.required, this.validateDateRange.bind(this)]],
            typeOfVisit: [this.guestData.typeOfVisit, [Validators.required]],
            typeOfVisitName: [this.guestData.typeOfVisitName],
            purposeOfVisit: [this.guestData.purposeOfVisit],
            purposeOfVisitName: [this.guestData.purposeOfVisitName],
            notification: [this.guestData.notification],
            entityOrFssId: [String(this.guestData.entityOrFssId)],
            entityOrFssName: [String(this.guestData.entityOrFssName)],
            approvers: [this.guestData.approvers, [Validators.required]],
            approversFull: [this.guestData.approversFull],
            ircApprovers: [this.guestData.ircApprovers],
            approversFullIRC: [this.guestData.approversFullIRC], // internal
            photoInfo: [this.guestData.photoInfo],
            attachmentGuid: [this.guestData.attachmentGuid],
            isBadgeExtension: [(this.guestData as any).isBadgeExtension || false] // Preserve badge extension flag
        });
        if (this.isSeniorManager) {
                  this.addGuestForm.controls.approvers.clearValidators();
                  this.addGuestForm.updateValueAndValidity();
            }
        this.calculateNumbersOfDays();

        if (this.guestData.photoInfo) {
            this.userPhotoUrl = this.guestData.photoInfo['file']?.url;
        }
        this.selectedApprovers = this.guestData.approvers;
        this.selectedIRCApprovers = this.guestData.ircApprovers;
        let popupShown = false;
        this.addGuestForm.valueChanges.subscribe(() => {
            this.formChangeHandler();
               const formValues = this.addGuestForm.value;
               const isClient = formValues.typeOfVisit === NonDeloitteTypeOfVisitEnum.Client;
                const isBusiness = formValues.purposeOfVisit === NonDeloittePurposeOfVisitEnum.BusinessVisit;
            
                if (isClient && isBusiness && !popupShown) {
                    popupShown = true;
                    this.showBusinessVisitPopup();
            
                    // Reset the flag after the popup is closed
                    setTimeout(() => (popupShown = false), 5000);
                }
        });

        this.addGuestForm.markAllAsTouched();
        this.addGuestForm.updateValueAndValidity();
        
    }

    // @ts-ignore
    editGuest(formValue: IContractorRequest): void {
        const formValue1 = {
            ...this.addGuestForm.value,
            visitStartDate: this.addGuestForm.value.visitStartDate.toString(),
            visitEndDate: this.addGuestForm.value.visitEndDate.toString(),
            approvers: this.isSeniorManager ? [this.user.userId] : this.addGuestForm.value.approvers
        } as IContractorRequest;
        
        this.guestEdited.emit({ ...formValue1, isValid: true });
        this.resetForm();
    }

    submit(): void {
        this.editGuest(this.addGuestForm.value as IContractorRequest);
    }
    setIRCApprovers(approverIds: number[]): void {
        const previousApprovers = this.addGuestForm.value.ircApprovers || [];
        this.addGuestForm.patchValue({
            ircApprovers: approverIds
        });
        if (approverIds.length !== previousApprovers.length) {
            this.addGuestForm.markAsDirty();
        }
        this.onFormChange();
    }
    
    setFullIRCApprovers(approvers: ISearchEmployeeApprovers[]): void {
        const previousApprovers = this.addGuestForm.value.approversFullIRC || [];
        this.addGuestForm.patchValue({
            approversFullIRC: approvers
        });
        if (approvers.length !== previousApprovers.length) {
            this.addGuestForm.markAsDirty();
        }
        this.onFormChange();
    }

    onTypeOfVisit(): void {
        const purpose = this.addGuestForm.get('purposeOfVisit');    
        purpose.setValidators(this.isPurposeVisible ? [Validators.required] : []);
        const ircApprovers = this.addGuestForm.controls.ircApprovers;
        if (!this.isPurposeVisible) {
            this.addGuestForm.controls.ircApprovers.setValue([]);
            this.addGuestForm.controls.approversFullIRC.setValue([]);
            ircApprovers.setValidators([]);
            ircApprovers.updateValueAndValidity();
            this.addGuestForm.updateValueAndValidity();
        }
        purpose.updateValueAndValidity();

        // Mark the form as dirty when the visitor type changes
        this.addGuestForm.markAsDirty();
        this.onFormChange(); // Call formChangeHandler to update the formDirty status
    }
    
    onFormChange() {
        this.formDirty.emit(this.addGuestForm.dirty);
    }

    onAccessRequiredChange(): void {
        this.addGuestForm.markAsDirty(); // Mark the form as dirty when access required changes
        this.onFormChange(); // Call onFormChange to update the formDirty status
    }

    validateDateRange(control: any): { [key: string]: boolean } | null {
        if (!this.addGuestForm) {
            return null;
        }
        
        const startDate = this.addGuestForm.get('visitStartDate')?.value;
        const endDate = control.value;
        
        if (!startDate || !endDate) {
            return null;
        }
        
        const start = new Date(startDate);
        const end = new Date(endDate);
        const diffTime = end.getTime() - start.getTime();
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
        
        if (diffDays > 90) {
            return { maxDaysExceeded: true };
        }
        
        return null;
    }

    dateChanged(): void {
        const start = Date.parse(this.addGuestForm.value.visitStartDate);
        const end = Date.parse(this.addGuestForm.value.visitEndDate);
        
        if (start) {
            this.minDate = this.addGuestForm.value.visitStartDate;
        }
        
        if (start && end && start > end) {
            this.addGuestForm.patchValue({
                visitEndDate: null
            });
        }
        
        // Revalidate end date when start date changes and mark as touched to show errors
        const endDateControl = this.addGuestForm.get('visitEndDate');
        endDateControl?.markAsTouched();
        endDateControl?.markAsDirty();
        endDateControl?.updateValueAndValidity();
        
        this.calculateNumbersOfDays();
    }
}
